import type { AnyListenerPredicate, UnknownAction, Dispatch, ListenerEffectAPI } from '@reduxjs/toolkit';

import type { PredicatorOptions } from './types';

type NonFalsy<T> = T extends null | undefined | false | 0 | '' ? never : T;

type TruthySamples<T extends readonly unknown[]> = {
  readonly [K in keyof T]: NonFalsy<T[K]>;
};

/**
 * **Predicator** is a class designed to simplify creating middleware listeners.
 * */
export class Predicator<RootState> {
  /**
   * **all** is a method that creates a predicate function that checks if all the samples returned by the probe function are set.
   * Depending on the mode, it also checks if the samples have changed.
   * @param probe - A function that returns an array of samples to be checked.
   * @param options - An object that contains the mode of comparison.
   * @param options.mode - A string that defines the mode of comparison. It can be either 'change' or 'once'. Default is 'once'. If set to 'change' it predicate will return true if all samples are set and at least one of them has changed.
   * @returns A predicate function that checks if all the samples are set and if they have changed.
   * */
  all<Samples extends readonly unknown[]>(
    probe: (state: RootState) => Samples,
    options: PredicatorOptions = {
      mode: 'once',
    }
  ): AnyListenerPredicate<RootState> {
    return (_action, currentState, previousState) => {
      const currentSamples = probe(currentState);
      const allGotSet = currentSamples.every((sample) => !!sample);
      // First, we check if all samples even are set
      if (!allGotSet) return false;
      const previousSamples = probe(previousState);
      // Then we check if the samples have changed, based on comparison mode
      if (options.mode === 'once') {
        // If the mode is "once", we check if any of the samples were not set in the previous state.
        // Because at this point we know that all are set in the current state.
        // And if all are not set in the previous state, then we know that they have settled as a result of current state change.
        return previousSamples.some((sample) => !sample);
      } else {
        // If mode is difference, we check if any of the samples have changed.
        return currentSamples.some((sample, index) => sample !== previousSamples[index]);
      }
    };
  }

  /**
   * **createListener** creates a complete listener configuration that can be directly spread into startListening.
   * The predicate uses the same logic as `all()` method, and the effect receives both the typed, truthy samples and
   * standard listener parameters from the probe function.
   * @param probe - A function that returns an array of samples to be checked.
   * @param handler - The handler function that receives typed samples, action and effectAPI.
   * @param options - An object that contains the mode of comparison.
   * @param options.mode - A string that defines the mode of comparison. It can be either 'change' or 'once'. Default is 'once'.
   * @returns A complete listener configuration object ready to be spread into startListening.
   */
  createListener<Samples extends ReadonlyArray<unknown>, EffectAPI extends ListenerEffectAPI<RootState, Dispatch>>(
    probe: (state: RootState) => Samples,
    handler: (samples: TruthySamples<Samples>, action: UnknownAction, effectAPI: EffectAPI) => void,
    options: PredicatorOptions = {
      mode: 'once',
    }
  ): {
    predicate: AnyListenerPredicate<RootState>;
    effect: (action: UnknownAction, effectAPI: EffectAPI) => void;
  } {
    return {
      predicate: this.all(probe, options),
      effect: (action, effectAPI): void => {
        const currentState = effectAPI.getState();
        const samples = probe(currentState);

        // Since predicate already validated all samples are truthy,
        // we can safely cast them to TruthySamples
        const truthySamples = samples as TruthySamples<Samples>;
        handler(truthySamples, action, effectAPI);
      },
    };
  }
}
